import React, { useEffect, useState } from "react";
import { Plus, Archive, Trash2, RotateCcw, TrendingUp, TrendingDown, Pencil, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { formatTRY, formatDate, todayISO } from "@/lib/format";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import MoneyInput from "@/components/MoneyInput";

export default function Muhasebe() {
  const { hasPerm } = useAuth();
  const [tab, setTab] = useState("active");
  const [items, setItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [cariler, setCariler] = useState([]);
  const [selected, setSelected] = useState(new Set());
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({
    type: "gelir", amount: 0, category: "", date: todayISO(), description: "", project_id: "", cari_id: "",
  });

  const resetForm = () => {
    setForm({ type: "gelir", amount: 0, category: "", date: todayISO(), description: "", project_id: "", cari_id: "" });
    setEditingId(null);
  };

  const openCreate = () => { resetForm(); setOpen(true); };

  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({
      type: it.type || "gelir",
      amount: it.amount || 0,
      category: it.category || "",
      date: it.date || todayISO(),
      description: it.description || "",
      project_id: it.project_id || "",
      cari_id: it.cari_id || "",
    });
    setOpen(true);
  };

  const load = async () => {
    const [{ data: t }, { data: p }, { data: c }] = await Promise.all([
      api.get(`/transactions?archived=${tab === "archive"}`),
      api.get("/projects"),
      api.get("/cariler"),
    ]);
    setItems(t);
    setProjects(p);
    setCariler(c);
    setSelected(new Set());
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab]);

  const totals = items.reduce(
    (acc, i) => {
      if (i.type === "gelir") acc.gelir += i.amount;
      else acc.gider += i.amount;
      return acc;
    },
    { gelir: 0, gider: 0 }
  );

  const removeOne = async (id) => {
    if (!window.confirm("Bu kaydı arşive taşımak istediğinize emin misiniz?")) return;
    try {
      await api.delete(`/transactions/${id}`);
      toast.success("Arşive taşındı");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        amount: Number(form.amount),
        project_id: form.project_id || null,
        cari_id: form.cari_id || null,
      };
      if (editingId) {
        await api.patch(`/transactions/${editingId}`, payload);
        toast.success("İşlem güncellendi");
      } else {
        await api.post("/transactions", payload);
        toast.success("İşlem eklendi");
      }
      setOpen(false);
      resetForm();
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const bulkDelete = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} kaydı arşive taşımak istediğinize emin misiniz?`)) return;
    await api.post("/transactions/bulk-delete", { ids: Array.from(selected) });
    toast.success("Kayıtlar arşivlendi");
    load();
  };

  const bulkRestore = async () => {
    if (selected.size === 0) return;
    await api.post("/transactions/bulk-restore", { ids: Array.from(selected) });
    toast.success("Kayıtlar geri getirildi");
    load();
  };

  const bulkPurge = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`${selected.size} kaydı KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.`)) return;
    await api.post("/transactions/bulk-purge", { ids: Array.from(selected) });
    toast.success("Kayıtlar kalıcı olarak silindi");
    load();
  };

  const restore = async (id) => {
    await api.post(`/transactions/${id}/restore`);
    toast.success("Geri yüklendi");
    load();
  };

  const purge = async (id) => {
    if (!window.confirm("Bu kaydı KALICI olarak silmek istediğinize emin misiniz? Bu işlem geri alınamaz.")) return;
    await api.delete(`/transactions/${id}/purge`);
    toast.success("Kalıcı olarak silindi");
    load();
  };

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  return (
    <div className="space-y-6" data-testid="muhasebe-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground font-semibold">Finans</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight mt-2">Muhasebe</h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          {selected.size > 0 && tab === "active" && hasPerm("muhasebe.delete") && (
            <Button variant="destructive" onClick={bulkDelete} data-testid="bulk-delete-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Arşivle ({selected.size})
            </Button>
          )}
          {selected.size > 0 && tab === "archive" && hasPerm("muhasebe.edit") && (
            <Button variant="outline" onClick={bulkRestore} data-testid="bulk-restore-btn">
              <RotateCcw className="w-4 h-4 mr-2" /> Geri Getir ({selected.size})
            </Button>
          )}
          {selected.size > 0 && tab === "archive" && hasPerm("muhasebe.delete") && (
            <Button variant="destructive" onClick={bulkPurge} data-testid="bulk-purge-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Kalıcı Sil ({selected.size})
            </Button>
          )}
          {hasPerm("muhasebe.view") && (
            <Button
              variant="outline"
              data-testid="tx-export-btn"
              onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/reports/transactions/excel?archived=${tab === "archive"}`, "_blank", "noopener")}
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" /> Excel İndir
            </Button>
          )}
          {hasPerm("muhasebe.create") && (
            <Button data-testid="new-tx-btn" onClick={() => setOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Yeni Kayıt
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4 shadow-none rounded-md border-l-4 border-l-[hsl(var(--success))]">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp className="w-4 h-4 text-[hsl(var(--success))]" />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Gelir</span>
          </div>
          <div className="font-heading text-2xl font-bold tabular-nums">{formatTRY(totals.gelir)}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md border-l-4 border-l-[hsl(var(--danger))]">
          <div className="flex items-center gap-2 mb-2">
            <TrendingDown className="w-4 h-4 text-[hsl(var(--danger))]" />
            <span className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">Gider</span>
          </div>
          <div className="font-heading text-2xl font-bold tabular-nums">{formatTRY(totals.gider)}</div>
        </Card>
        <Card className="p-4 shadow-none rounded-md border-l-4 border-l-brand">
          <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground mb-2">Net</div>
          <div className={`font-heading text-2xl font-bold tabular-nums ${totals.gelir - totals.gider >= 0 ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
            {formatTRY(totals.gelir - totals.gider)}
          </div>
        </Card>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="active">Aktif</TabsTrigger>
          <TabsTrigger value="archive"><Archive className="w-3 h-3 mr-1" /> Arşiv</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="shadow-none rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-secondary/50">
                <TableHead className="w-10">
                  {tab === "active" && hasPerm("muhasebe.delete") && items.length > 0 && (
                    <Checkbox
                      data-testid="tx-select-all"
                      checked={selected.size > 0 && selected.size === items.length}
                      onCheckedChange={(checked) => {
                        setSelected(checked ? new Set(items.map((i) => i.id)) : new Set());
                      }}
                    />
                  )}
                </TableHead>
                <TableHead>Tarih</TableHead>
                <TableHead>Tür</TableHead>
                <TableHead>Kategori</TableHead>
                <TableHead>Açıklama</TableHead>
                <TableHead>Proje</TableHead>
                <TableHead>Cari</TableHead>
                <TableHead>Kaydeden</TableHead>
                <TableHead className="text-right">Tutar</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((it) => (
                <TableRow key={it.id} data-testid={`tx-row-${it.id}`} className="hover:bg-secondary/40">
                  <TableCell>
                    <Checkbox checked={selected.has(it.id)} onCheckedChange={() => toggle(it.id)} data-testid={`tx-check-${it.id}`} />
                  </TableCell>
                  <TableCell className="tabular-nums text-xs">{formatDate(it.date)}</TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded font-semibold ${it.type === "gelir" ? "bg-[hsl(var(--success))]/15 text-[hsl(var(--success))]" : "bg-[hsl(var(--danger))]/15 text-[hsl(var(--danger))]"}`}>
                      {it.type === "gelir" ? "Gelir" : "Gider"}
                    </span>
                  </TableCell>
                  <TableCell className="text-sm">{it.category || "-"}</TableCell>
                  <TableCell className="text-sm text-muted-foreground max-w-xs truncate">{it.description || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{projects.find((p) => p.id === it.project_id)?.name || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{cariler.find((c) => c.id === it.cari_id)?.name || "-"}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{it.created_by_name || "-"}</TableCell>
                  <TableCell className={`text-right tabular-nums font-medium ${it.type === "gelir" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                    {it.type === "gelir" ? "+" : "−"} {formatTRY(it.amount)}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1 justify-end">
                      {tab === "active" && hasPerm("muhasebe.edit") && (
                        <Button size="sm" variant="ghost" onClick={() => openEdit(it)} className="h-8 w-8 p-0" title="Düzenle" data-testid={`tx-edit-${it.id}`}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "active" && hasPerm("muhasebe.delete") && (
                        <Button size="sm" variant="ghost" onClick={() => removeOne(it.id)} className="h-8 w-8 p-0 text-destructive" title="Arşive Taşı" data-testid={`tx-delete-${it.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "archive" && hasPerm("muhasebe.edit") && (
                        <Button size="sm" variant="ghost" onClick={() => restore(it.id)} className="h-8 w-8 p-0" title="Geri Getir" data-testid={`tx-restore-${it.id}`}>
                          <RotateCcw className="w-3 h-3" />
                        </Button>
                      )}
                      {tab === "archive" && hasPerm("muhasebe.delete") && (
                        <Button size="sm" variant="ghost" onClick={() => purge(it.id)} className="h-8 w-8 p-0 text-destructive" title="Kalıcı Sil" data-testid={`tx-purge-${it.id}`}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {items.length === 0 && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-8">Kayıt yok.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-heading">{editingId ? "İşlem Düzenle" : "Yeni İşlem"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tür</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger data-testid="tx-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gelir">Gelir</SelectItem>
                    <SelectItem value="gider">Gider</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Tarih</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Tutar</Label><MoneyInput data-testid="tx-amount" value={form.amount} onChange={(n) => setForm({ ...form, amount: n })} /></div>
              <div className="space-y-2"><Label>Kategori</Label><Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Örn: İşçilik" /></div>
            </div>
            <div className="space-y-2"><Label>Açıklama</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Proje</Label>
                <Select value={form.project_id || "none"} onValueChange={(v) => setForm({ ...form, project_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yok —</SelectItem>
                    {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Cari</Label>
                <Select value={form.cari_id || "none"} onValueChange={(v) => setForm({ ...form, cari_id: v === "none" ? "" : v })}>
                  <SelectTrigger><SelectValue placeholder="Seç" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Yok —</SelectItem>
                    {cariler.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button data-testid="tx-save" onClick={save} disabled={!form.amount}>{editingId ? "Kaydet" : "Ekle"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
